import React, { useRef } from "react";
import Logo from "../ui/Logo";
import Modal from "../ui/Modal";
import { motion } from "framer-motion";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, getImageUrl } from "../services/api";
import { Link } from "react-router-dom";
import Developer from "../component/footerData/Developer";
import ResourceLinks from "../component/footerData/ResourceLinks";
import HomeIcons from "../component/homeData/HomeIcons";

gsap.registerPlugin(ScrollTrigger);

const fetchFooterSettings = async () => {
  try {
    const { data } = await apiRequest("/settings/footer_config");
    return data && data.value ? (typeof data.value === 'string' ? JSON.parse(data.value) : data.value) : {};
  } catch (error) {
    console.error("Failed to fetch footer settings", error);
    return {};
  }
};

const Footer = ({ setOpenModal, openModal, onClose }) => {
  const curYear = new Date().getFullYear();
  const footerRef = useRef(null);

  const { data: settings } = useQuery({
    queryKey: ["footer_settings"],
    queryFn: fetchFooterSettings,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  useGSAP(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: footerRef.current,
        start: "top 90%",
      },
    });

    tl.from(".footer-col", {
      y: 50,
      opacity: 0,
      duration: 0.8,
      stagger: 0.2,
      ease: "power3.out",
    });
  }, { scope: footerRef });

  const iconVariants = {
    hover: {
      scale: 1.2,
      originX: 0,
      color: "#ff014f", // designColor match
      textShadow: "0px 0px 8px rgb(255, 1, 79)",
      transition: {
        type: "spring",
        stiffness: 300,
      },
    },
  };

  return (
    <>
      <div ref={footerRef} className="w-full border-t border-white/5 bg-metal-pattern py-10 font-primary dark:bg-bodyColor dark:text-lightText relative overflow-hidden">
        {/* Decorative Gloss */}
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className=" mb-8 grid grid-cols-1 gap-y-10 pt-8 sm:grid-cols-2 sm:justify-items-center sm:gap-y-12 md:grid-cols-3">

            {/* Column 1: Logo & Socials */}
            <div className="footer-col self-start">
              {settings?.logo ? (
                <Link to={"/"} className="block w-40">
                  <img
                    src={getImageUrl(settings.logo)}
                    alt="Logo"
                    loading="lazy"
                    className="w-full h-auto object-contain"
                  />
                </Link>
              ) : (
                <Logo />
              )}

              {settings?.social_links && settings.social_links.length > 0 ? (
                <div className="mt-8">
                  <motion.h2 className="mb-2 uppercase text-lg font-normal">
                    Find me on
                  </motion.h2>
                  <div className="flex gap-4 text-gray-700 dark:text-white flex-wrap">
                    {settings.social_links.map((link, index) => (
                      <motion.div
                        key={index}
                        className="socialIcons"
                        variants={iconVariants}
                        whileHover="hover"
                      >
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="outline-none p-3 rounded-full bg-slate-100 dark:bg-gray-800 hover:bg-designColor hover:text-white transition-colors duration-300 w-12 h-12 flex items-center justify-center text-lg"
                        >
                          {link.icon_class ? (
                            <i className={link.icon_class}></i>
                          ) : (
                            <span className="text-xs font-bold">{link.platform?.substring(0, 2)}</span>
                          )}
                        </a>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  <HomeIcons />
                </div>
              )}
            </div>

            {/* Dynamic Menu Columns OR Default Columns */}
            {settings?.menu_columns && settings.menu_columns.length > 0 ? (
              settings.menu_columns.map((col, colIndex) => (
                <div key={colIndex} className="footer-col self-start">
                  <h2 className="text-2xl text-designColor mb-8 uppercase font-semibold">{col.title}</h2>
                  <ul className="flex flex-col gap-4">
                    {col.links?.map((link, linkIndex) => (
                      <motion.li
                        key={linkIndex}
                        whileHover={{ scale: 1.05, originX: 0, color: "#ff014f" }}
                        transition={{ type: "spring", stiffness: 400 }}
                        className="text-lg dark:text-lightText text-gray-500 capitalize font-medium"
                      >
                        {link.url && link.url.startsWith('http') ? (
                          <a href={link.url} target="_blank" rel="noreferrer" className="hover:text-designColor">
                            {link.label}
                          </a>
                        ) : (
                          <Link to={link.url || '#'} onClick={(!link.url || link.url === '#') ? () => setOpenModal(true) : undefined} className="hover:text-designColor">
                            {link.label}
                          </Link>
                        )}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <>
                <div className="footer-col">
                  <Developer setOpenModal={setOpenModal} />
                </div>
                <div className="footer-col">
                  <ResourceLinks setOpenModal={setOpenModal} />
                </div>
              </>
            )}

          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-20 pt-8 border-t border-gray-200 dark:border-gray-800 text-center"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium tracking-wide">
              {settings?.copyright_text || <>&copy; {curYear} <span className="text-designColor">Osama Ali</span>. All rights reserved.</>}
            </p>
          </motion.div>
        </div>
      </div>
      {openModal && (
        <Modal onClose={onClose} title={"Coming soon"}>
          Sorry! These pages are under construction. We will build those pages
          as soon as possible. If you need any contact information or any
          material you can contact us through website or directly through email.
          Thank You
        </Modal>
      )}
    </>
  );
};

export default Footer;
